/**
 * Citation export.
 *
 * Every field a reference manager needs — title, authors, year, pages, DOI —
 * already lives in `public/data/volumes/<year>.json`, so a `.bib` file is a
 * pure function of an article record. Nothing is stored on the server and no
 * request is made: the reader clicks "Cite", the browser formats the text and
 * hands it back as a download.
 *
 *   import { formatCitation, citationFilename, CITATION_FORMATS } from './citations.js';
 *   formatCitation(article, '2026', 'bibtex');   // -> "@article{chesneau2026exploring, …}"
 *
 * If you add a format, add it to CITATION_FORMATS and the modal picks it up.
 */

// Journal-level constants. These mirror scripts/prerender.js — if the journal
// is ever renamed or re-ISSN'd, both files need the new value.
const JOURNAL = 'e-Journal of Analysis and Applied Mathematics';
const JOURNAL_ABBREV = 'e-JAAM';
const ISSN = '2544-9990';
const PUBLISHER = 'EJAAM';

// The site presents one volume per year and labels it by year, exactly as
// prerender.js does. Switch to sequential volume numbers in both places.
const volumeLabel = (year) => String(year);

// ---------------------------------------------------------------------------
// Parsing the record
// ---------------------------------------------------------------------------

const MONTHS = {
  jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
  jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
};

const MONTH_NAMES = {
  '01': 'jan', '02': 'feb', '03': 'mar', '04': 'apr', '05': 'may', '06': 'jun',
  '07': 'jul', '08': 'aug', '09': 'sep', '10': 'oct', '11': 'nov', '12': 'dec',
};

/** "1 Feb 2026" -> { year, month: '02', day: '01', iso: '2026-02-01' } */
export function parseDate(value, fallbackYear) {
  const m = /^(\d{1,2})\s+([A-Za-z]{3})[a-z]*\s+(\d{4})$/.exec(String(value || '').trim());
  if (!m) {
    return { year: String(fallbackYear), month: '', day: '', iso: `${fallbackYear}` };
  }
  const day = m[1].padStart(2, '0');
  const month = MONTHS[m[2].toLowerCase()] || '';
  return {
    year: m[3],
    month,
    day,
    iso: month ? `${m[3]}-${month}-${day}` : m[3],
  };
}

/** "1 - 22" -> { first: '1', last: '22' } */
export function parsePages(value) {
  const m = /^\s*(\d+)\s*-\s*(\d+)\s*$/.exec(String(value || ''));
  return m ? { first: m[1], last: m[2] } : { first: '', last: '' };
}

/**
 * The JSON stores the DOI display form, "doi.org/10.62780/ejaam/2026-001".
 * Reference managers want the bare DOI; links want the resolver in front.
 */
export function parseDoi(value) {
  const bare = String(value || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^(dx\.)?doi\.org\//i, '')
    .replace(/^doi:/i, '');
  return { doi: bare, url: bare ? `https://doi.org/${bare}` : '' };
}

/**
 * Split a display name into BibTeX's "Family, Given" order.
 *
 * Names in the data are written naturally ("Christophe Chesneau"), so the last
 * word is the family name — except for the lowercase particles that belong to
 * it ("Ludwig van Beethoven" -> "van Beethoven, Ludwig"). A name already
 * written "Chesneau, Christophe" is passed through untouched.
 */
export function splitName(name) {
  // A stray trailing comma once turned "Mısır J. Mardanov," into a single
  // family name — strip edge punctuation before reading anything into it.
  const clean = String(name || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[,;]+|[,;]+$/g, '')
    .trim();
  if (!clean) return { family: '', given: '' };

  if (clean.includes(',')) {
    const [family, ...rest] = clean.split(',');
    return { family: family.trim(), given: rest.join(',').trim() };
  }

  const parts = clean.split(' ');
  if (parts.length === 1) return { family: parts[0], given: '' };

  // Walk back over trailing particles: "de la Vallée Poussin" stays whole.
  let cut = parts.length - 1;
  const PARTICLES = ['van', 'von', 'de', 'del', 'della', 'di', 'da', 'dos', 'du',
    'la', 'le', 'el', 'al', 'bin', 'ibn', 'ter', 'ten', 'op'];
  while (cut > 1 && PARTICLES.includes(parts[cut - 1].toLowerCase())) cut -= 1;

  return { family: parts.slice(cut).join(' '), given: parts.slice(0, cut).join(' ') };
}

/** "Christophe" -> "C."   "Jean-Pierre" -> "J.-P." */
function initials(given) {
  return String(given)
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      word
        .split('-')
        .map((piece) => (piece ? `${piece[0]}.` : ''))
        .join('-')
    )
    .join(' ');
}

// ---------------------------------------------------------------------------
// Text handling
// ---------------------------------------------------------------------------

/**
 * Titles are stored as LaTeX ("On $p$-convexification of …"). BibTeX wants
 * that verbatim; RIS and the plain-text citation want readable prose. This is
 * the same reduction prerender.js applies to meta tags.
 */
export function stripLatex(input) {
  return String(input || '')
    .replace(/\$\$?([^$]*)\$\$?/g, '$1')
    .replace(/\\(?:text|mathrm|mathbf|mathit|mathcal|mathbb|emph|textbf|textit)\{([^{}]*)\}/g, '$1')
    .replace(/\\[a-zA-Z]+\s*/g, (cmd) => {
      const greek = {
        '\\alpha': 'α', '\\beta': 'β', '\\gamma': 'γ', '\\delta': 'δ',
        '\\epsilon': 'ε', '\\varepsilon': 'ε', '\\zeta': 'ζ', '\\eta': 'η',
        '\\theta': 'θ', '\\lambda': 'λ', '\\mu': 'μ', '\\nu': 'ν', '\\xi': 'ξ',
        '\\pi': 'π', '\\rho': 'ρ', '\\sigma': 'σ', '\\tau': 'τ', '\\phi': 'φ',
        '\\varphi': 'φ', '\\chi': 'χ', '\\psi': 'ψ', '\\omega': 'ω',
        '\\Gamma': 'Γ', '\\Delta': 'Δ', '\\Theta': 'Θ', '\\Lambda': 'Λ',
        '\\Sigma': 'Σ', '\\Phi': 'Φ', '\\Psi': 'Ψ', '\\Omega': 'Ω',
        '\\infty': '∞', '\\leq': '≤', '\\geq': '≥', '\\neq': '≠',
        '\\to': '→', '\\times': '×', '\\cdot': '·', '\\pm': '±',
        '\\in': '∈', '\\subset': '⊂', '\\subseteq': '⊆', '\\cup': '∪',
        '\\cap': '∩', '\\partial': '∂', '\\nabla': '∇', '\\sum': '∑',
        '\\int': '∫', '\\prod': '∏', '\\sqrt': '√',
      };
      return greek[cmd.trim()] !== undefined ? greek[cmd.trim()] : '';
    })
    .replace(/[{}\\^_]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Escape the three characters that break a .bib file, but only outside math.
 * "Sobolev & Besov spaces" must become "Sobolev \& Besov spaces", while
 * "$a \& b$" — and every backslash command inside math — is left alone.
 */
function escapeBibtex(input) {
  return String(input || '')
    .split(/(\$[^$]*\$)/)
    .map((chunk, i) => (i % 2 ? chunk : chunk.replace(/([&%#])/g, '\\$1')))
    .join('');
}

/** Fold accents to ASCII letters and drop everything else — cite keys only. */
function asciiLetters(input) {
  return String(input || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/[đð]/gi, 'd')
    .replace(/ł/gi, 'l')
    .replace(/ø/gi, 'o')
    // NFD leaves these alone — they are distinct letters, not accented ones.
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'I')
    .replace(/[əƏ]/g, 'e')
    .replace(/[æÆ]/g, 'ae')
    .replace(/[œŒ]/g, 'oe')
    .replace(/[^A-Za-z0-9]/g, '');
}

/** "van Beethoven" -> "VanBeethoven";  "Šarkovskii" -> "Sarkovskii" */
function surnameForKey(family) {
  const ascii = asciiLetters(family);
  return ascii ? ascii[0].toUpperCase() + ascii.slice(1) : '';
}

/** 0 -> "a", 25 -> "z", 26 -> "aa" — the suffix on a repeated key. */
function suffixLetter(index) {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(97 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

/**
 * "Chesneau_2026" — the first author's surname and the volume year.
 *
 * The volume year, not the online publication year: volume 2018 contains four
 * papers that went online in July 2019, and they are cited — and DOI'd
 * (ejaam-2018-0004) — as 2018. Keying them to 2019 would both mislabel them
 * and collide with the real 2019 volume.
 */
function baseKey(article, year) {
  const first = ((article && article.authors) || [])[0];
  const surname = surnameForKey(splitName(first && first.name).family) || 'EJAAM';
  return `${surname}_${String(year)}`;
}

/**
 * The BibTeX cite key: `Surname_Year` for the first author — "Chesneau_2026",
 * where the year is the volume's.
 *
 * Surname and year alone are not unique: an author with two papers in the same
 * volume would produce the same key twice, and BibTeX refuses a file with a
 * duplicate key. So when the volume's other articles are passed in, the ones
 * that share a key get an "a", "b", … suffix in page order — the convention
 * readers already expect from "Smith 2020a". With no siblings to compare
 * against, the bare key is returned; every article published so far is unique
 * on `Surname_Year` alone.
 *
 * @param {Object} article
 * @param {string|number} year
 * @param {Array} [siblings] the other articles in the same volume
 */
export function citationKey(article, year, siblings) {
  const base = baseKey(article, year);
  if (!siblings || siblings.length < 2) return base;

  const sharing = siblings.filter((other) => baseKey(other, year) === base);
  if (sharing.length < 2) return base;

  const index = sharing.findIndex(
    (other) => other === article || other.documentName === article.documentName
  );
  return index < 0 ? base : `${base}${suffixLetter(index)}`;
}

/** "10.62780-ejaam-2026-001.bib" — one file per article, matching the PDF. */
export function citationFilename(article, year, formatId) {
  const format = CITATION_FORMATS.find((f) => f.id === formatId);
  const base =
    String(article.documentName || '').replace(/\.pdf$/i, '') ||
    citationKey(article, year);
  return `${base}.${format ? format.extension : 'txt'}`;
}

// ---------------------------------------------------------------------------
// Formats
// ---------------------------------------------------------------------------

function toBibtex(article, year, siblings) {
  const date = parseDate(article.publishedOnline, year);
  const pages = parsePages(article.pageRange);
  const { doi, url } = parseDoi(article.doi);

  const authors = (article.authors || [])
    .map((author) => {
      const { family, given } = splitName(author.name);
      return given ? `${family}, ${given}` : family;
    })
    .filter(Boolean)
    .join(' and ');

  // The extra braces around the title stop BibTeX styles from lowercasing
  // proper nouns — "Hardy-Hilbert" must not become "hardy-hilbert".
  // An article belongs to the year of its volume even when it appeared online
  // later, so that is the year in the reference. When the two disagree, the
  // month would read as a date that never happened ("jul 2018") — drop it and
  // record the real appearance in a note instead.
  const volumeYear = String(year);
  const appearedLater = date.year !== volumeYear;

  const fields = [
    ['author', authors],
    ['title', `{${escapeBibtex(article.title)}}`],
    ['journal', JOURNAL],
    ['volume', volumeLabel(year)],
    ['year', volumeYear],
    ['month', !appearedLater && date.month ? MONTH_NAMES[date.month] : ''],
    ['pages', pages.first ? `${pages.first}--${pages.last}` : ''],
    ['issn', ISSN],
    ['doi', doi],
    ['url', url || article.path],
    ['publisher', PUBLISHER],
    ['note', appearedLater && article.publishedOnline
      ? `Published online ${article.publishedOnline}`
      : ''],
  ].filter(([, value]) => value);

  const width = Math.max(...fields.map(([name]) => name.length));
  const body = fields
    .map(([name, value]) => `  ${name.padEnd(width)} = {${value}}`)
    .join(',\n');

  return `@article{${citationKey(article, year, siblings)},\n${body}\n}\n`;
}

function toRis(article, year) {
  const date = parseDate(article.publishedOnline, year);
  const pages = parsePages(article.pageRange);
  const { doi, url } = parseDoi(article.doi);
  const abstract = [].concat(article.abstract || []).map(stripLatex).join(' ');

  const lines = [['TY', 'JOUR']];

  (article.authors || []).forEach((author) => {
    const { family, given } = splitName(author.name);
    lines.push(['AU', given ? `${family}, ${given}` : family]);
  });

  lines.push(
    ['TI', stripLatex(article.title)],
    ['T2', JOURNAL],
    ['J2', JOURNAL_ABBREV],
    ['VL', volumeLabel(year)],
    ['SP', pages.first],
    ['EP', pages.last],
    ['PY', String(year)],
    ['DA', date.month ? `${date.year}/${date.month}/${date.day}` : date.year],
    ['AB', abstract],
    ['SN', ISSN],
    ['DO', doi],
    ['UR', url || article.path],
    ['PB', PUBLISHER],
    ['LA', 'en']
  );

  (article.keywords || []).forEach((keyword) => lines.push(['KW', keyword]));

  lines.push(['ER', '']);

  // RIS is a CRLF format, and every tag is padded to two characters + "  - ".
  return lines
    .filter(([tag, value]) => value || tag === 'ER')
    .map(([tag, value]) => `${tag}  - ${value}`)
    .join('\r\n') + '\r\n';
}

function toPlainText(article, year) {
  const pages = parsePages(article.pageRange);
  const { url } = parseDoi(article.doi);

  const names = (article.authors || []).map((author) => {
    const { family, given } = splitName(author.name);
    return given ? `${initials(given)} ${family}` : family;
  });

  const authors =
    names.length <= 1
      ? names.join('')
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

  const range = pages.first ? `, ${pages.first}–${pages.last}` : '';

  // "Journal 12 (2025)" is the usual shape, but this journal numbers its
  // volumes by year — printing "2025 (2025)" reads as a mistake, so the
  // repetition collapses to one. Sequential volume numbers get both back.
  const label = volumeLabel(year);
  const where =
    label === String(year) ? `${JOURNAL} ${year}` : `${JOURNAL} ${label} (${year})`;

  return [
    authors && `${authors}, `,
    `${stripLatex(article.title)}, `,
    `${where}${range}.`,
    url && ` ${url}`,
  ]
    .filter(Boolean)
    .join('') + '\n';
}

/**
 * The formats the Cite dialog offers, in tab order. `mime` is what the Blob is
 * labelled with on download — browsers use it to decide whether to open or save.
 */
export const CITATION_FORMATS = [
  {
    id: 'bibtex',
    label: 'BibTeX',
    extension: 'bib',
    mime: 'application/x-bibtex;charset=utf-8',
    hint: 'For LaTeX. Requires a UTF-8 capable engine, or \\usepackage[utf8]{inputenc}.',
    build: toBibtex,
  },
  {
    id: 'ris',
    label: 'RIS',
    extension: 'ris',
    mime: 'application/x-research-info-systems;charset=utf-8',
    hint: 'For Zotero, Mendeley, EndNote, RefWorks and Papers.',
    build: toRis,
  },
  {
    id: 'text',
    label: 'Plain text',
    extension: 'txt',
    mime: 'text/plain;charset=utf-8',
    hint: 'A ready-made reference to paste into an email or a Word document.',
    build: toPlainText,
  },
];

/**
 * Render one article in one format.
 *
 * @param {Object} article  a record from a volume JSON file
 * @param {string|number} year  the volume the article belongs to
 * @param {string} formatId  'bibtex' | 'ris' | 'text'
 * @param {Array} [siblings]  the volume's other articles, so a repeated
 *   `Surname_Year` key can be disambiguated. Optional — see citationKey.
 * @returns {string}
 */
export function formatCitation(article, year, formatId, siblings) {
  const format = CITATION_FORMATS.find((f) => f.id === formatId);
  if (!format || !article) return '';
  return format.build(article, year, siblings);
}

/**
 * A whole volume in one file — the same records, concatenated in page order,
 * so a reader can pull a year's worth of references in a single click.
 */
export function formatVolumeCitations(articles, year, formatId) {
  const separator = formatId === 'bibtex' ? '\n' : formatId === 'ris' ? '\r\n' : '\n';
  return (articles || [])
    .map((article) => formatCitation(article, year, formatId, articles))
    .join(separator);
}
